import HBIcon from "@/components/HBIcon";
import HBSwiper from "@/components/HBSwiper";
import { ImageMargin, Radius } from "@/pageComponents/MaterialCalendar/cover";
import { MaterialTopicCardD } from "@/pageComponents/MaterialTopicCard";
import { getTopicsPreview } from "@/services";
import { getSearch } from "@/tracker/shared/global";
import { putFirst } from "@/utils/arr";
import { buildSearchParams } from "@/utils/gotoTopicSearch";
import { useCreation, useSafeState, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  RecommendTopic,
  RecommendTopicsProps,
} from "../RecommendTopicList";
import { randomTopicCover } from "../RecommendTopicList";
import styles from "./index.module.less";

export default function RecommendMiddle({
  searchWord,
  topics,
  columnSize = 240,
  columnNum = 5,
}: {
  searchWord: string;
  topics: RecommendTopicsProps;
  columnSize?: number;
  columnNum?: number;
}) {
  const {
    title,
    subTitle = "高质量素材，商用 0 风险",
    recommend = [],
  } = topics || {};

  const swiperRef = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  // 获取搜索相关数据
  const searchData = getSearch();
  // 构建跳转参数
  const searchParams = useMemo(() => {
    return buildSearchParams(searchWord, { searchData });
  }, [searchData, searchWord]);

  /** 过滤没有筛选器和预览封面图片为空的, 同时将符合匹配搜索词内容提前: 相等放最前面*/
  const filterDataSource = (data: RecommendTopic[]) => {
    return putFirst(
      putFirst(
        data.filter((item) => item.filter && item.preview?.pins?.length),
        (item) => item.filter.name.includes(searchWord)
      ),
      (item) => item.filter.name === searchWord
    );
  };

  const searchTitle = title || searchWord;

  // 第一次渲染时随机封面
  useCreation(() => {
    randomTopicCover(recommend[0]);
  }, []);

  const [current, setCurrent] = useState(recommend[0]);
  const [dataSource, setDataSource] = useSafeState(
    filterDataSource(recommend[0]?.recommend || [])
  );

  // 当不需要滚动时，专题列表居中对齐
  useEffect(() => {
    if (dataSource.length > 0) {
      const timer = setTimeout(() => {
        const swiperElement = document.querySelector(
          `.${styles.swipper}`
        ) as HTMLElement;

        if (swiperElement) {
          setNeedsScroll(swiperElement.scrollWidth > swiperElement.offsetWidth);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [dataSource, columnSize, columnNum]);

  // 搜索其他内容时，推荐内容更新，重置current
  useUpdateEffect(() => {
    setCurrent(randomTopicCover(recommend[0]));
  }, [recommend]);

  useUpdateEffect(() => {
    // 第一个搜索时已经默认返回了，不需要再去查询
    if (current === recommend[0]) {
      setDataSource(filterDataSource(recommend[0].recommend));
      return;
    }

    // 查询主题预览信息
    getTopicsPreview({
      preview_limit: 4,
      filter_ids: current.recommend?.map((filter) => filter.filter_id),
    }).then((res) => {
      const dataSource = filterDataSource(res.filters).map((filter) => {
        const recommend =
          current.recommend.find(
            (item) => item.filter_id === filter.filter_id
          ) || {};
        return {
          ...recommend,
          ...filter,
        };
      });

      setDataSource(dataSource);
    });
  }, [current]);

  if (dataSource.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(styles.recommend)}
      style={{
        width: columnSize * columnNum + 16 * (columnNum - 1),
      }}
    >
      <div className={styles.left}>
        <div className={styles.leftContent}>
          {searchTitle && <div className={styles.title}># {searchTitle}</div>}
          <div className={styles.subtitle}>{subTitle}</div>
        </div>
        <div className={styles.leftBtn}>
          <div
            className={styles.more}
            onClick={() => {
              window.open(
                dataSource[0].urlname
                  ? `/topics/${dataSource[0].urlname}-${
                      dataSource[0].filter_id
                    }${searchParams ? `?${searchParams}` : ""}`
                  : dataSource[0].link,
                "_blank"
              );
            }}
            data-gd-expose="button_expose"
            data-gd-click="button_click"
            data-button-name="探索更多"
            data-button-source={`推荐主题_${current?.name}_探索更多`}
            data-module-name="腰部推荐卡片"
            data-subject-name="素材主题资源位"
            data-search-word={searchWord}
            data-search-result-number={searchData?.search_result_number || 0}
            data-module-location={searchData?.banner_index || 0}
          >
            探索更多
            <HBIcon type="ic_chevron_right" />
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.right, {
          [styles.rightContent]: !needsScroll,
        })}
      >
        <HBSwiper
          ref={swiperRef}
          className={styles.swipper}
          iconClassName={styles.swiperIcon}
          contentClassName={styles.swiperContent}
        >
          {dataSource.map((item, index) => {
            return (
              <MaterialTopicCardD
                title={item?.origin?.title}
                radius={Radius.outer}
                margin={ImageMargin.small}
                key={index}
                pinCount={item.preview.pin_count}
                pins={item.preview.pins}
                filter={item.filter}
                link={
                  item.urlname
                    ? `/topics/${item.urlname}-${item.filter_id}${
                        searchParams ? `?${searchParams}` : ""
                      }`
                    : item.link
                }
                data-gd-expose="resource_loc_expose"
                data-gd-click="resource_loc_click"
                data-resource-id={item.filter.id}
                data-resource-type="二级主题"
                data-resource-title={item.filter.name}
                data-content-source={`推荐主题_${current?.name}_${item.filter.name}`}
                data-resource-location={index + 1}
                data-module-name="腰部推荐卡片"
                data-subject-name="素材主题资源位"
                data-search-result-number={
                  searchData?.search_result_number || 0
                }
                data-module-location={searchData?.banner_index || 0}
              />
            );
          })}
        </HBSwiper>
      </div>
    </div>
  );
}
